import { allProjects, ACTIVE_STATUSES } from '../db.js';
import { hasToken, resumeToken, ensureToken, listChildren, searchProjectFolders } from '../google-api.js';
import { walkTree, listingsFromWalk, shapeProject, isSupersededPath, pickProjectFolder } from '../drive-folders.js';
import { checkName, isIgnoredFile, isCheckedPath, isIssuable, CHECKED_FOLDERS, AUTO_FILED_FOLDERS } from '../doc-convention.js';
import { openFile } from './google-panels.js';
import { escapeHtml, refTail, safeUrl } from '../ui.js';
import { navIcon } from '../nav-icons.js';
import { renderDrives } from './files-browse.js';
import { renderPhotos } from './files-photos.js';
import { renderProjectColumns } from './files-columns.js';
import { swrMany, driveKey, ageText, seedCache, driveStore } from '../drive-cache.js';
import { startDriveLive } from '../drive-live.js';
import { updatedNote } from '../live-refresh.js';

// FILES (Karan 06/10/2026, "Track web — Mail and Files"). Two tabs in the
// standard section layout — a figure with its eyebrow, one line, one primary
// ("Open Drive"), then the tables:
//
//   Shared drives  the drives as they sit in Google (files-browse.js). The
//                  screen opens here (Karan 18/10).
//   My projects    the real template folders of the projects you lead.
//
// The naming check (ASP's HOUSE rule PROJECT-FOLDER-DOCUMENT_REV) used to be
// this screen's front page, as a firm-wide "to fix before issue" list. Karan
// (18/10) couldn't tell what it was for: it lives on each project page now
// ("Before issue"), where the question is actually asked. evaluate() below is
// still what both use.
//
// Track never renames, moves or deletes a file. It reads Drive as the person
// and reports; ticking a row RE-READS that file from Drive and only turns it
// green if Drive now has a name that follows the rule.
//
// Which projects: the one opened (a project page's Files link passes openId),
// the person's own live projects (they lead them, status In Progress → Part
// Issued), and any they add with "+ Add a project" (remembered on this
// device). With none of those, the project they looked at last. Capped rather
// than "every live project in the firm".
//
// SPEED (Karan 06/10: "it takes ages to load drive"). The screen paints from
// what Drive said last time (drive-cache.js, per person) and reads Drive again
// behind it; and the reading is ONE walk across every chosen project, a level
// at a time, each level one Drive call for up to forty folders (walkTree +
// listChildren) — it used to be one call per folder, every project racing.
//
// A folder opens IN Track (files-folder.js); Google Drive is the "Open Drive"
// button or a folder's own "Open in Drive" link, never a row click.
//
// User-facing text says "project", never "job" (Karan 06/10). Code names keep
// "job" where they always had it.
//
// House rule: every value from Drive or Firestore goes through escapeHtml(),
// every data-built href through safeUrl().

const pad2 = (n) => String(n).padStart(2, '0');
const LAST_PROJ_KEY = 'fl-last-project';
const TAB_KEY = 'fx-tab';
// True for the first Files render after the browser reloaded a Files page
// only (a reload on Home, then the menu to Files, starts fresh).
let reloaded = (() => {
  try { const n = performance.getEntriesByType('navigation')[0]; return n?.type === 'reload' && new URL(n.name).pathname.startsWith('/files'); } catch (_) { return false; }
})();
const EXTRA_KEY = 'fl-jobs';
const MAX_JOBS = 8;
const WORDS = ['No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];
const word = (n) => (n <= 10 ? WORDS[n] : String(n));
const plural = (n, one, many = `${one}s`) => (n === 1 ? one : many);

function dayShort(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return 'Today';
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}${d.getFullYear() === now.getFullYear() ? '' : `/${String(d.getFullYear()).slice(-2)}`}`;
}

function readExtra() {
  try { const v = JSON.parse(localStorage.getItem(EXTRA_KEY) || '[]'); return Array.isArray(v) ? v.map(String) : []; } catch (_) { return []; }
}
function writeExtra(list) {
  try { localStorage.setItem(EXTRA_KEY, JSON.stringify(list.slice(-MAX_JOBS))); } catch (_) { /* private mode */ }
}

/** The jobs Files shows — see the header. Exported for the harness and tests. */
export function pickJobs(projects, { email = '', openId = null, extra = [], remembered = null } = {}) {
  const num = (p) => String(p.number || '');
  const byNum = new Map(projects.map((p) => [num(p), p]));
  const me = String(email).split('@')[0].toLowerCase();
  const numVal = (p) => { const m = num(p).match(/\d+/); return m ? parseInt(m[0], 10) : -1; };
  const out = [];
  const add = (p) => { if (p && !out.some((x) => num(x) === num(p))) out.push(p); };
  if (openId) add(byNum.get(String(openId)));
  projects.filter((p) => ACTIVE_STATUSES.includes(p.status) && String(refTail(p.lead || '')).toLowerCase() === me)
    .sort((a, b) => numVal(b) - numVal(a)).slice(0, 5).forEach(add);
  extra.forEach((n) => add(byNum.get(String(n))));
  if (!out.length) add((remembered && byNum.get(String(remembered))) || projects.slice().sort((a, b) => numVal(b) - numVal(a))[0]);
  return out.slice(0, MAX_JOBS);
}

// ── The chosen projects' Drive folders, read ────────────────────────────────
// Each project's folder (or, with no id on record, the one named by its number
// — read-only, nothing written back), its top-level folders, and every file
// under each, walked a few levels down as ASP files them ("Working
// Calculations"). ONE walk for all of them: a level of every tree per Drive
// round trip. The result is plain data, kept in the per-person cache.
async function resolveFolder(project) {
  if (project.folderId) return { folderId: project.folderId, folderUrl: project.folderUrl || '' };
  const hit = pickProjectFolder(project.number, await searchProjectFolders(project.number).catch(() => []));
  if (!hit.folder) return { error: 'nofolder' };
  return {
    folderId: hit.folder.id, folderUrl: hit.folder.webViewLink || '',
    foundNote: `found on the drive by its number as “${hit.folder.name}”${hit.others.length ? ` (newest of ${hit.others.length + 1})` : ''}, not linked in Track yet`,
  };
}

export async function loadProjects(projects, who) {
  const heads = await Promise.all(projects.map((p) => resolveFolder(p).catch((e) => ({ error: (e && e.message) || 'Drive could not be read' }))));
  const roots = heads.map((h, i) => (h.folderId ? { id: h.folderId, key: String(i) } : null)).filter(Boolean);
  const walked = roots.length ? await walkTree(listChildren, roots, { maxDepth: 4, maxFolders: 300 }) : new Map();
  // The walk read every folder's listing on the way down — keep them, so a
  // folder opened in Track (files-folder.js) paints without asking Drive.
  roots.forEach((r) => { const w = walked.get(r.key); if (w) seedCache(who, listingsFromWalk(r.id, w)); });
  return heads.map((h, i) => shapeProject(h, walked.get(String(i)), { ignore: isIgnoredFile }));
}

// ── Derive the document-control picture from what was read ─────────────────
// overrides: fileId → { name } once a re-check has read a newer name from Drive.
export function evaluate(job, overrides = new Map()) {
  const number = String(job.project.number);
  const nameOf = (f) => (overrides.get(f.id) || {}).name || f.name;
  const allFiles = job.folders.flatMap((fo) => fo.files.map((f) => ({ f, fo })));
  const siblings = allFiles.map(({ f }) => nameOf(f));
  const checked = [];
  const folders = job.folders.map((fo) => {
    const current = fo.files.filter((f) => !isSupersededPath(f.path));
    const latest = current.slice().sort((a, b) => String(b.modifiedTime || '').localeCompare(String(a.modifiedTime || '')))[0] || null;
    let fixes = 0, held = 0;
    for (const f of current) {
      if (!isCheckedPath(fo.code, f.path) || !isIssuable(nameOf(f))) continue;
      held++;
      const name = nameOf(f);
      const r = checkName(name, { job: number, folderCode: fo.code, siblings, self: name });
      const row = { job: number, file: f, folder: fo, name, result: r, renamed: overrides.has(f.id) && overrides.get(f.id).name !== f.name };
      checked.push(row);
      if (!r.ok) fixes++;
    }
    let dc;
    if (AUTO_FILED_FOLDERS.includes(fo.code)) dc = { label: 'Auto-filed', tone: 'grey' };
    else if (!CHECKED_FOLDERS.includes(fo.code)) dc = { label: 'Not checked', tone: 'grey' };
    else if (fixes) dc = { label: `${fixes} to fix`, tone: 'attn' };
    else if (!held) dc = { label: 'Empty', tone: ['04', '05'].includes(fo.code) ? 'attn' : 'grey' };
    else dc = { label: 'Matches', tone: 'ok' };
    // The latest file under its name as Drive last said it (a re-check may have read a newer one).
    return { fo, count: current.length, latest: latest && { ...latest, name: nameOf(latest) }, dc };
  });
  return { checked, folders, fixes: checked.filter((c) => !c.result.ok), matches: checked.filter((c) => c.result.ok) };
}

// ── Markup pieces ───────────────────────────────────────────────────────────
// v4.0.0 (design export p15): "Files", then the search, Open in Drive and the
// tabs. No figure or eyebrow. Upload and the Recent tab are drawn in the design
// but Track writes nothing to Drive from Files and keeps no recent-files list
// yet, so they stay out until it does.
const SHARED_DRIVES = 'https://drive.google.com/drive/shared-drives';
const headHtml = () => `<div class="page-h fl4-head"><h1>Files</h1>
    <span class="fl4-head-acts">
      <label class="fl4-search">${navIcon('search', 16)}<input type="search" id="fl4-q" placeholder="Search names on this page" aria-label="Search names on this page" autocomplete="off" spellcheck="false"></label>
      <a class="btn" id="fl4-drive" href="${SHARED_DRIVES}" target="_blank" rel="noopener">Open in Drive</a>
    </span></div>`;
const tabsHtml = (tab) => `<div class="fx-tabs" role="tablist">
    <button type="button" role="tab" class="fx-tab" data-tab="projects" aria-selected="${tab === 'projects'}">My projects</button>
    <button type="button" role="tab" class="fx-tab" data-tab="drives" aria-selected="${tab === 'drives'}">Shared drives</button>
  </div>`;
const skel = (w, h) => `<span class="fm-skel" style="width:${w};height:${h};"></span>`;

function connectCard() {
  return `<div class="empty-state fl4-connect">
      <h2>Connect Google Drive to see the project folders</h2>
      <p>Track reads Drive as you, with your own permissions, and never renames, moves or deletes a file.</p>
      <button type="button" id="fx-connect" class="btn primary">Connect Google Drive</button>
    </div>`;
}

/** The head's search: hides the rows on screen whose text does not match (names, numbers, kinds). */
function wireSearch(mount) {
  const q = mount.querySelector('#fl4-q');
  const body = mount.querySelector('#fx-body');
  if (!q || !body) return;
  const apply = () => {
    const n = q.value.trim().toLowerCase();
    body.querySelectorAll('.fl4-row[data-go], .fx-row[data-go], .fx-row[data-i]').forEach((r) => { r.hidden = !!n && !r.textContent.toLowerCase().includes(n); });
  };
  q.addEventListener('input', apply);
  new MutationObserver(() => { if (q.value) apply(); }).observe(body, { childList: true, subtree: true });
}

// ── The screen ──────────────────────────────────────────────────────────────
export async function renderFiles(mount, session, opts = {}) {
  // Opens on the drives; a project link (/files/8571) opens that project's folders.
  // A refresh comes back to the tab you were on.
  const restore = reloaded; reloaded = false;
  let savedTab = null;
  if (restore) { try { savedTab = sessionStorage.getItem(TAB_KEY); } catch (_) { /* private mode */ } }
  const tab = opts.tab || (opts.openId ? 'projects' : (savedTab === 'projects' ? 'projects' : 'drives'));
  try { sessionStorage.setItem(TAB_KEY, tab); } catch (_) { /* private mode */ }
  const onOpenProject = opts.onOpenProject;

  // Immediate frame before any await (house rule): the real layout, figure and
  // line as skeletons at their true size, so nothing shifts when Drive answers.
  mount.innerHTML = `<div class="fx fl4">
      ${headHtml()}
      ${tabsHtml(tab)}
      <div id="fx-body"></div>
    </div>`;
  wireSearch(mount);
  // Switching tabs quickly (or leaving Files) starts a new render while this
  // one is still waiting on Drive. Only the LATEST render, on its own root,
  // may paint — else the Projects register lands on top of Shared drives.
  const token = {};
  mount.__fxRender = token;
  const root = mount.querySelector('.fx');
  const current = () => root.isConnected && mount.__fxRender === token;
  if (mount.__driveLive) { mount.__driveLive(); mount.__driveLive = null; }
  const wireTabs = () => mount.querySelectorAll('.fx-tab').forEach((b) => b.addEventListener('click', () => {
    if (b.dataset.tab !== tab) renderFiles(mount, session, { ...opts, tab: b.dataset.tab });
  }));
  wireTabs();

  // An expired token is not a disconnection: renew it quietly first.
  if (!hasToken()) await resumeToken();
  if (!current()) return;
  if (!hasToken()) {
    mount.querySelector('#fx-body').innerHTML = connectCard();
    const btn = mount.querySelector('#fx-connect');
    if (btn) btn.addEventListener('click', () => {
      ensureToken().then(() => renderFiles(mount, session, opts)).catch(() => { btn.textContent = 'Google connection cancelled — try again'; });
    });
    return;
  }

  const projects = await allProjects().catch(() => []);
  if (!current()) return;
  // The levels below report where "Open in Drive" should now go (the old hero's
  // figure and line are gone in v4; an empty project list still says so).
  const setHero = (h) => {
    if (!current() || !h) return;
    const a = mount.querySelector('#fl4-drive');
    if (a) a.href = safeUrl(h.driveHref || SHARED_DRIVES);
    if (h.empty) { const b = mount.querySelector('#fx-body'); if (b) b.innerHTML = `<p class="fl4-empty">${escapeHtml(h.empty)}</p>`; }
  };

  if (tab === 'drives') {
    renderDrives(mount.querySelector('#fx-body'), session, {
      projects, setHero, restore,
      // The naming check lives on the project page now (18/10).
      onCheckJob: onOpenProject ? (number) => {
        const p = projects.find((x) => String(x.number) === String(number));
        if (p) onOpenProject(p.id, String(number));
      } : null,
    });
    return;
  }

  let remembered = null;
  try { remembered = localStorage.getItem(LAST_PROJ_KEY); } catch (_) { /* private mode */ }
  const jobs = pickJobs(projects, { email: session && session.email, openId: opts.openId, extra: readExtra(), remembered });
  if (!jobs.length) {
    setHero({ empty: 'You don’t lead any live projects. Add one to see its folders here.' });
    return;
  }
  if (opts.openId) { try { localStorage.setItem(LAST_PROJ_KEY, String(opts.openId)); } catch (_) { /* private mode */ } }

  // Skeleton tables until the first answer — from the cache, usually at once.
  mount.querySelector('#fx-body').innerHTML = `<div class="fx-table">${[0, 1, 2, 3].map(() => `<div class="fx-row fx-g-fold">${skel('60%', '13px')}${skel('24px', '12px')}${skel('40px', '12px')}</div>`).join('')}</div>`;
  const loaded = jobs.map((p) => ({ project: p, pending: true }));

  const state = {
    chip: opts.openId && jobs.some((p) => String(p.number) === String(opts.openId)) && jobs.length > 1 ? String(opts.openId) : 'All',
    overrides: new Map(),   // fileId → { name } re-read from Drive
    fresh: { cached: false, at: null, checking: true, failed: false },
  };

  const draw = () => {
    if (!current()) return;
    const evals = loaded.map((j) => ({ job: j, ev: j.pending || j.error ? null : evaluate(j, state.overrides) }));
    const inView = evals.filter(({ job }) => state.chip === 'All' || String(job.project.number) === state.chip);
    const pendingAll = inView.length && inView.every(({ job }) => job.pending);
    const filesIn = ({ ev }) => (ev ? ev.folders.reduce((a, f) => a + f.count, 0) : 0);
    const chips = [['All', evals.reduce((a, e) => a + filesIn(e), 0)], ...evals.map((e) => [String(e.job.project.number), e.job.pending ? '·' : filesIn(e)])];
    const nFiles = inView.reduce((a, e) => a + filesIn(e), 0);
    const line = pendingAll ? 'Reading the project folders from Drive…'
      : `${nFiles.toLocaleString('en-GB')} ${plural(nFiles, 'file')} across ${inView.length === 1 ? `${inView[0].job.project.number}’s folders` : `${word(inView.length).toLowerCase()} projects`}. Each project page checks the file names before issue.`;

    const body = mount.querySelector('#fx-body');
    setHero({ fig: pad2(inView.length), eyebrow: inView.length === 1 ? 'Project' : 'Your projects', line });
    body.innerHTML = `
      <div class="fx">
        <div class="fx-chips" role="group" aria-label="Projects">
          ${chips.map(([k, n]) => `<button type="button" class="fx-chip" data-chip="${escapeHtml(k)}" aria-pressed="${state.chip === k}">${escapeHtml(k)}<span class="fx-chip-n">${escapeHtml(String(n))}</span></button>`).join('')}
          <button type="button" class="fx-chip fx-chip-add" id="fx-add">+ Add a project</button>
        </div>
        ${foldersTable(inView)}
      </div>`;
    wire(body);
  };

  function foldersTable(inView) {
    const groups = inView.map(({ job, ev }) => {
      const p = job.project;
      const label = `${p.number} ${p.clientName || p.title || ''}`.trim();
      const head = (n, total) => `<div class="fx-group">
          ${onOpenProject && p.id ? `<button type="button" class="fx-group-label" data-open-project="${escapeHtml(p.id)}" data-num="${escapeHtml(String(p.number))}">${escapeHtml(label)}</button>` : `<span class="fx-group-label">${escapeHtml(label)}</span>`}
          <span class="fx-group-n">${escapeHtml(n)}</span><span class="fx-group-total">${escapeHtml(total)}</span>
        </div>`;
      if (job.pending) return head('', '') + [0, 1, 2].map(() => `<div class="fx-row fx-g-fold">${skel('50%', '13px')}${skel('24px', '12px')}${skel('40px', '12px')}</div>`).join('');
      if (job.error === 'nofolder') return head('', '') + `<div class="fx-row fx-g-fold is-attn"><span class="fx-cell fx-wrap" style="grid-column:1/-1;font-size:13px;color:var(--ink-2);">No Drive folder linked, and none named ${escapeHtml(String(p.number))} on the drives. Add its Folder ID in Projects.</span></div>`;
      if (job.error) return head('', '') + `<div class="fx-row fx-g-fold is-attn"><span class="fx-cell fx-wrap" style="grid-column:1/-1;font-size:13px;color:var(--ink-2);">Drive could not be read: ${escapeHtml(job.error)}. Reconnect and try again.</span></div>`;
      const total = ev.folders.reduce((a, f) => a + f.count, 0) + job.loose.length;
      const rows = ev.folders.map(({ fo, count, latest }) => `
        <div class="fx-row fx-g-fold" data-go="folder" data-job="${escapeHtml(String(p.number))}" data-folder="${escapeHtml(fo.id)}">
          <span class="fx-cell"><span class="fx-stack"><span class="fx-t">${escapeHtml(fo.name)}</span>${latest ? `<button type="button" class="fx-open" data-open-file="${escapeHtml(latest.id)}" data-job="${escapeHtml(String(p.number))}" title="Open ${escapeHtml(latest.name)}">${escapeHtml(latest.name)}</button>` : '<span class="fx-sub">Nothing filed</span>'}</span></span>
          <span class="fx-cell r fx-mono fx-c-files">${escapeHtml(String(count))}</span>
          <span class="fx-cell r fx-mono fx-c-upd">${escapeHtml(latest ? dayShort(latest.modifiedTime) : '—')}</span>
        </div>`).join('');
      const notes = [job.foundNote ? `${p.number} ${job.foundNote}.` : '', job.unread ? `${job.unread} ${plural(job.unread, 'folder')} on ${p.number} could not be read.` : '',
        job.partial ? `Some very deep folders on ${p.number} are not listed; open the drive for those.` : '',
        job.loose.length ? `${job.loose.length} loose ${plural(job.loose.length, 'file')} at the top of ${p.number}, outside the numbered folders.` : ''].filter(Boolean);
      return head(`${ev.folders.length} ${plural(ev.folders.length, 'folder')}`, `${total} ${plural(total, 'file')}`) + rows
        + (notes.length ? `<div class="fx-foot fx-wrap">${escapeHtml(notes.join(' '))}</div>` : '');
    }).join('');
    return `<div class="fx-table">
        <div class="fx-table-head"><span class="fx-table-title">Folders</span><span class="fx-table-note" id="fx-fresh">${escapeHtml(freshNote())}</span></div>
        <div class="fx-cols fx-g-fold"><span>Folder</span><span class="r">Files</span><span class="r">Updated</span></div>
        ${groups}
      </div>`;
  }

  // What the Folders table says about how current it is.
  function freshNote() {
    const f = state.fresh;
    if (f.failed) return `Drive could not be reached. This is what it showed ${ageText(f.at)}.`;
    if (f.cached && f.checking) return `What Drive showed ${ageText(f.at)}. Checking again…`;
    if (f.checking) return 'Reading Drive…';
    return updatedNote(f.liveAt) || 'Folders open here in Track, with your own Drive permissions.';
  }

  // ── Behaviour ────────────────────────────────────────────────────────────
  const fileById = (id) => {
    for (const j of loaded) for (const fo of j.folders || []) { const f = fo.files.find((x) => x.id === id); if (f) return { f, fo, job: j }; }
    for (const j of loaded) { const f = (j.loose || []).find((x) => x.id === id); if (f) return { f, fo: null, job: j }; }
    return null;
  };

  function openOne(id, jobNumber) {
    const hit = fileById(id);
    if (!hit) return;
    const reviewerFor = (f, i) => {
      const h = fileById(f.id) || hit;
      const job = order[i]?.job ?? jobNumber;
      return { reference: f.name, title: f.name, status: isSupersededPath(h.f.path) ? 'Superseded' : 'Current',
        user: (session && (session.displayName || session.email)) || '', email: (session && session.email) || null, projectNo: String(job || '') };
    };
    // ← / → move along the list as it is on screen.
    const order = [...mount.querySelectorAll('[data-open-file]')].map((b) => ({ id: b.dataset.openFile, job: b.dataset.job })).filter((x, k, arr) => arr.findIndex((y) => y.id === x.id) === k);
    const siblings = order.map((x) => fileById(x.id)?.f).filter(Boolean);
    openFile(hit.f, { reviewer: reviewerFor(hit.f, order.findIndex((x) => x.id === id)), reviewerFor, siblings });
  }

  function wire(body) {
    body.querySelectorAll('[data-chip]').forEach((b) => b.addEventListener('click', () => { state.chip = b.dataset.chip; draw(); }));
    const addBtn = body.querySelector('#fx-add');
    if (addBtn) addBtn.addEventListener('click', () => openJobPicker(addBtn, projects, jobs, (n) => {
      writeExtra([...readExtra().filter((x) => x !== String(n)), String(n)]);
      renderFiles(mount, session, { ...opts, tab: 'projects', openId: n });
    }));
    body.querySelectorAll('[data-open-project]').forEach((b) => b.addEventListener('click', () => onOpenProject(b.dataset.openProject, b.dataset.num)));
    body.querySelectorAll('[data-open-file]').forEach((b) => b.addEventListener('click', (e) => { e.stopPropagation(); openOne(b.dataset.openFile, b.dataset.job); }));
    body.querySelectorAll('.fx-row[data-go="folder"]').forEach((r) => r.addEventListener('click', () => {
      const job = loaded.find((j) => String(j.project.number) === r.dataset.job);
      const fo = job && job.folders.find((x) => x.id === r.dataset.folder);
      if (!fo) return;
      // 7. Images is the photo grid; every other folder opens HERE, in Track.
      if (fo.code === '07') {
        renderPhotos(mount, session, { project: { ...job.project, folderId: job.folderId }, projectNumber: job.project.number,
          onBack: () => renderFiles(mount, session, opts) });
        return;
      }
      openFolder(job, fo);
    }));
  }

  // A project folder opens the project in COLUMNS (turn 8, 8a–8c): folders |
  // the folder as a register | preview. Back (← Projects, ⌫ or the crumb)
  // returns to the register, which repaints from the cache at once.
  function openFolder(job, fo) {
    const p = job.project;
    const host = mount.querySelector('#fx-body');
    if (!host) return;
    mount.querySelectorAll('.fx-tabs').forEach((el) => { el.hidden = true; });
    const ev = evaluate(job, state.overrides);
    const total = ev.folders.reduce((a, f) => a + f.count, 0) + (job.loose || []).length;
    const site = p.siteAddress && typeof p.siteAddress === 'object'
      ? [p.siteAddress.line1, p.siteAddress.postcode && !String(p.siteAddress.line1 || '').includes(p.siteAddress.postcode) ? p.siteAddress.postcode : ''].filter(Boolean).join(' ') : String(p.siteAddress || p.site || '');
    const summary = [p.title && p.title !== p.clientName ? p.title : '', site,
      `${total} ${plural(total, 'file')}`].filter(Boolean).join(' · ');
    renderProjectColumns(host, {
      session, job, folderId: fo.id, summary, onOpenProject,
      onBack: () => renderFiles(mount, session, opts),
      onPhotos: () => renderPhotos(mount, session, { project: { ...p, folderId: job.folderId }, projectNumber: p.number,
        onBack: () => renderFiles(mount, session, opts) }),
    });
  }

  // Paint from the cache (per person, per project), then read Drive again.
  const keys = jobs.map((p) => driveKey(session && session.email, 'project', String(p.number), String(p.folderId || 'by-number')));
  try {
    await swrMany({
      keys,
      fetchAll: () => loadProjects(jobs, session && session.email),
      onData: (vals, meta) => {
        vals.forEach((v, i) => { if (v) loaded[i] = { ...v, project: jobs[i] }; });
        const ats = meta.ats.filter(Boolean);
        state.fresh = { cached: meta.cached, at: ats.length ? Math.min(...ats) : Date.now(), checking: Boolean(meta.cached && meta.revalidating), failed: false };
        draw();
      },
      onError: () => { state.fresh = { ...state.fresh, checking: false, failed: true }; draw(); },
    });
  } catch (e) {
    loaded.forEach((j, i) => { if (j.pending) loaded[i] = { project: jobs[i], error: (e && e.message) || 'Drive could not be read', folders: [], loose: [] }; });
    state.fresh = { cached: false, at: Date.now(), checking: false, failed: false };
    draw();
  }
  // A cached paint whose revalidation was skipped (it was seconds old) is current.
  if (!current()) return;
  if (state.fresh.checking) { state.fresh.checking = false; const n = mount.querySelector('#fx-fresh'); if (n) n.textContent = freshNote(); }

  // LIVE (07/10/2026): Drive's changes feed every 30 s while this is on screen
  // and visible. A change inside a project's tree re-reads THAT project only,
  // stores it, and repaints the figure, the to-fix list and the folder counts.
  const who = session && session.email;
  if (mount.__driveLive) mount.__driveLive();      // one feed per screen, however often it is redrawn
  mount.__driveLive = startDriveLive({
    mount, who,
    watched: () => loaded.flatMap((j) => (j && j.watchIds) || []),
    known: () => Object.assign({}, ...loaded.map((j) => (j && j.known) || {})),
    onTouched: async (ids) => {
      const hit = new Set(ids);
      const idx = loaded.map((j, i) => (j && (j.watchIds || []).some((x) => hit.has(x)) ? i : -1)).filter((i) => i >= 0);
      // Only while the Folders table is what is showing — never repaint the
      // register over a folder that has been opened, or over another tab.
      if (!idx.length || !mount.querySelector('#fx-fresh')) return;
      const fresh = await loadProjects(idx.map((i) => jobs[i]), who);
      if (!current() || !mount.querySelector('#fx-fresh')) return;
      const at = Date.now();
      idx.forEach((i, k) => {
        if (!fresh[k] || fresh[k].error) return;
        loaded[i] = { ...fresh[k], project: jobs[i] };
        driveStore.set(keys[i], { value: fresh[k], at }).catch(() => {});
      });
      state.fresh = { cached: false, at, checking: false, failed: false, liveAt: at };
      draw();
      setTimeout(() => { const n = mount.querySelector('#fx-fresh'); if (n && mount.isConnected) n.textContent = freshNote(); }, 61000);
    },
  });
}

// ── "+ Add a project" — a searchable popover over every project ─────────────
let pickerEl = null;
function closePicker() {
  if (!pickerEl) return;
  pickerEl.remove(); pickerEl = null;
  document.removeEventListener('keydown', pickerKey);
  document.removeEventListener('mousedown', pickerDoc, true);
}
function pickerKey(e) { if (e.key === 'Escape') { e.preventDefault(); closePicker(); } }
function pickerDoc(e) { if (pickerEl && !pickerEl.contains(e.target) && !pickerEl.__anchor.contains(e.target)) closePicker(); }
function openJobPicker(anchor, projects, current, onPick) {
  if (pickerEl) { closePicker(); return; }
  const shown = new Set(current.map((p) => String(p.number)));
  const numVal = (p) => { const m = String(p.number || '').match(/\d+/); return m ? parseInt(m[0], 10) : -1; };
  const list = projects.filter((p) => p.number && !shown.has(String(p.number))).sort((a, b) => numVal(b) - numVal(a));
  const el = document.createElement('div');
  el.__anchor = anchor;
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-label', 'Add a project');
  const r = anchor.getBoundingClientRect();
  const width = Math.min(360, window.innerWidth - 32);
  const left = Math.max(16, Math.min(r.left, window.innerWidth - width - 16));
  el.setAttribute('style', `position:fixed;top:${Math.round(r.bottom + 8)}px;left:${Math.round(left)}px;width:${width}px;z-index:60;background:var(--sheet-lift);border:1px solid var(--hair);border-radius:12px;box-shadow:var(--shadow-float);padding:8px;display:flex;flex-direction:column;gap:6px;`);
  el.innerHTML = `<input type="search" placeholder="Project number, client or site" aria-label="Find a project" style="border:1px solid var(--hair);border-radius:8px;padding:10px 12px;font:inherit;font-size:13px;background:var(--surface);color:var(--ink);">
    <div class="fx-pick-list" style="max-height:300px;overflow-y:auto;display:flex;flex-direction:column;"></div>`;
  document.body.appendChild(el);
  pickerEl = el;
  const input = el.querySelector('input');
  const box = el.querySelector('.fx-pick-list');
  const paint = () => {
    const q = input.value.trim().toLowerCase();
    const hits = list.filter((p) => !q || `${p.number} ${p.clientName || ''} ${p.title || ''}`.toLowerCase().includes(q)).slice(0, 40);
    box.innerHTML = hits.length ? hits.map((p) => `<button type="button" data-n="${escapeHtml(String(p.number))}" style="display:flex;gap:10px;align-items:baseline;text-align:left;min-height:40px;padding:8px 10px;border:0;border-radius:8px;background:none;cursor:pointer;font:inherit;color:var(--ink);">
        <span style="font-family:var(--font-technical);font-size:12.5px;color:var(--link);flex:none;">${escapeHtml(String(p.number))}</span>
        <span style="font-size:13px;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(p.clientName || p.title || '')}</span></button>`).join('')
      : '<span style="padding:10px;font-size:12.5px;color:var(--ink-3);">No project matches.</span>';
    box.querySelectorAll('[data-n]').forEach((b) => {
      b.addEventListener('mouseenter', () => { b.style.background = 'var(--nav-hover)'; });
      b.addEventListener('mouseleave', () => { b.style.background = 'none'; });
      b.addEventListener('click', () => { closePicker(); onPick(b.dataset.n); });
    });
  };
  input.addEventListener('input', paint);
  paint();
  input.focus();
  document.addEventListener('keydown', pickerKey);
  document.addEventListener('mousedown', pickerDoc, true);
}
